<template>
    <div class="booking-log-page">
        <div class="filters">
            <v-select
                v-model="searchFilters.noShowType"
                :items="noShowOptions"
                item-title="label"
                item-value="value"
                variant="outlined"
                label="Registreringstype"
            ></v-select>

            <v-menu
                v-model="dateFromMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                min-width="auto"
                offset-y
                transition="scale-transition"
            >
                <template #activator="{ props: itemProps }">
                    <v-text-field
                        v-model="searchFilters.dateFrom"
                        label="Dato fra"
                        append-inner-icon="mdi-calendar"
                        readonly
                        variant="outlined"
                        persistent-placeholder
                        v-bind="itemProps"
                    ></v-text-field>
                </template>
                <v-date-picker
                    v-model="dateFromPicker"
                    :max="searchFilters.dateTo"
                    show-adjacent-months
                    @update:model-value="dateFromMenu = false"
                ></v-date-picker>
            </v-menu>

            <v-menu
                v-model="dateToMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                min-width="auto"
                offset-y
                transition="scale-transition"
            >
                <template #activator="{ props: itemProps }">
                    <v-text-field
                        v-model="searchFilters.dateTo"
                        label="Dato til"
                        append-inner-icon="mdi-calendar"
                        readonly
                        variant="outlined"
                        persistent-placeholder
                        v-bind="itemProps"
                    ></v-text-field>
                </template>
                <v-date-picker
                    v-model="dateToPicker"
                    :min="searchFilters.dateFrom"
                    show-adjacent-months
                    @update:model-value="dateToMenu = false"
                ></v-date-picker>
            </v-menu>
        </div>
        <v-data-table
            :headers="headers"
            :items="tripsStore.bookingNoShowList"
            :items-per-page="tripsStore.bookingNoShowPaging.pageSize"
            :page="page"
            :loading="loadingTable"
            class="elevation-1"
        >
            <template #item.tripId="{ item }">
                <span>{{ `${item.departure} - ${item.destination}` }}</span>
            </template>
            <template #item.tripStop.passingTime="{ item }">
                <span>{{ ToLocalDate(item.tripStop.passingTime, 'time') }}</span>
            </template>
            <template #item.bookingNoShowType="{ item }">
                <span>{{ getNoShowType(item) }}</span>
            </template>
            <template #bottom>
                <div class="pagination-wrap">
                    <div class="paging-wrap">
                        <v-pagination v-model="page" :length="tripsStore.bookingNoShowPaging.pageCount"></v-pagination>
                    </div>
                </div>
            </template>
        </v-data-table>
    </div>
</template>

<script setup lang="ts">
import { useTripsStore } from '@/features/trips/tripsStore';
import { onMounted, ref, watch, computed, onBeforeUnmount } from 'vue';
import { BookingNoShowType } from './BookingNoShowModels';
import { ToLocalDate, FormatDateISO } from '@/helpers/DateFormatHelpers';
import { storeToRefs } from 'pinia';
import { useBaseStore } from '@/features/shared/baseStore';

const tripsStore = useTripsStore();
const baseStore = useBaseStore();
const { searchFilters } = storeToRefs(tripsStore);
const page = ref(null);
const loadingTable = ref(true);
const dateFromMenu = ref(false);
const dateToMenu = ref(false);

onMounted(async () => {
    baseStore.clearMenuItems();
    await tripsStore.loadBookingNoShows();
    page.value = tripsStore.bookingNoShowPaging.pageNumber;
    loadingTable.value = false;
});

const dateFromPicker = computed<Date>({
    get: () => {
        const val = searchFilters.value.dateFrom;
        return val ? new Date(val) : null;
    },
    set: (value: Date) => {
        searchFilters.value.dateFrom = FormatDateISO(value);
    }
});

const dateToPicker = computed<Date>({
    get: () => {
        const val = searchFilters.value.dateTo;
        return val ? new Date(val) : null;
    },
    set: (value: Date) => {
        searchFilters.value.dateTo = FormatDateISO(value);
    }
});

watch(page, async newPage => {
    if (newPage !== null) {
        loadingTable.value = true;
        await tripsStore.loadBookingNoShows(newPage);
        loadingTable.value = false;
    }
});

watch(
    searchFilters,
    async () => {
        loadingTable.value = true;
        await tripsStore.loadBookingNoShows();
        loadingTable.value = false;
    },
    { deep: true }
);

const headers = [
    { title: 'Dato', key: 'operatingDay' },
    { title: 'Tur', key: 'tripId' },
    { title: 'Tid', key: 'tripStop.passingTime' },
    { title: 'Kai', key: 'tripStop.stopName' },
    { title: 'Bestillinger', key: 'bookingCount' },
    { title: 'Registrert', key: 'bookingNoShowType' }
];

const noShowOptions = [
    { label: 'Alle', value: null },
    { label: 'Ingen reisende på kai', value: BookingNoShowType.NoTravellers },
    { label: 'For få reisende på kai', value: BookingNoShowType.TooFewTravellers }
];

function getNoShowType(item: any): string {
    switch (item.bookingNoShowType) {
        case BookingNoShowType.NoTravellers:
            return 'Ingen reisende på kai';
        case BookingNoShowType.TooFewTravellers:
            return 'For få reisende på kai';
        default:
            return 'Ukjent type';
    }
}

onBeforeUnmount(() => {
    searchFilters.value = {
        noShowType: null,
        dateFrom: null,
        dateTo: null,
        tripId: null,
        operatingDay: null
    };
    tripsStore.bookingNoShowPaging.pageNumber = 1;
});
</script>

<style scoped>
.booking-log-page {
    padding: 24px;
}

.filters {
    display: grid;
    gap: 16px;
    grid-template-columns: 30% 30% 30%;
}
</style>
